import { defineStore } from 'pinia'
import { ref } from 'vue'
import { notificationsApi } from '@/api/notifications'
import { useAuthStore } from '@/store/auth'
import type { NotificationMessageOut } from '@/types/api'

// 30s 轮询；document 隐藏时停止，可见时立即拉一次
const POLL_INTERVAL = 30_000
const RECENT_LIMIT = 3

export const useNotificationsStore = defineStore('notifications', () => {
  const authStore = useAuthStore()
  const unreadCount = ref(0)
  const recentUnread = ref<NotificationMessageOut[]>([])
  const loading = ref(false)

  let pollTimer: ReturnType<typeof setInterval> | null = null

  async function fetchOnce() {
    if (!authStore.isLoggedIn) return
    try {
      const [c, list] = await Promise.all([
        notificationsApi.unreadCount(),
        notificationsApi.list({ unread_only: true, limit: RECENT_LIMIT }),
      ])
      unreadCount.value = c.data.unread_count
      recentUnread.value = list.data.items
    } catch {
      // 401 由 axios 拦截器处理；其他错误静默（轮询期间不应打扰用户）
    }
  }

  function onVisibility() {
    if (document.hidden) {
      stopTimer()
    } else {
      // 切回前台：立即拉一次再重启定时器
      fetchOnce()
      startTimer()
    }
  }

  function startTimer() {
    if (pollTimer) return
    pollTimer = setInterval(fetchOnce, POLL_INTERVAL)
  }

  function stopTimer() {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  function startPolling() {
    if (!authStore.isLoggedIn) return
    if (pollTimer) return  // 已启动
    document.addEventListener('visibilitychange', onVisibility)
    fetchOnce()
    startTimer()
  }

  function stopPolling() {
    document.removeEventListener('visibilitychange', onVisibility)
    stopTimer()
  }

  /**
   * 清理数据（登出时调用）
   */
  function reset() {
    unreadCount.value = 0
    recentUnread.value = []
  }

  // ---- 单条操作（乐观更新） ----

  async function markRead(id: number) {
    const target = recentUnread.value.find((n) => n.id === id)
    if (!target) return
    if (target.is_read) return  // 已读
    // 乐观
    target.is_read = true
    target.read_at = new Date().toISOString()
    if (unreadCount.value > 0) unreadCount.value -= 1
    recentUnread.value = recentUnread.value.filter((n) => !n.is_read)
    try {
      await notificationsApi.markRead(id)
    } catch {
      // 失败回滚
      target.is_read = false
      target.read_at = null
      unreadCount.value += 1
      recentUnread.value.push(target)
    }
  }

  async function markAllRead() {
    const snapshot = recentUnread.value
    const prevCount = unreadCount.value
    // 乐观
    unreadCount.value = 0
    recentUnread.value = []
    try {
      await notificationsApi.markAllRead()
    } catch {
      unreadCount.value = prevCount
      recentUnread.value = snapshot
    }
  }

  async function remove(id: number) {
    const target = recentUnread.value.find((n) => n.id === id)
    if (target && target.is_read === false && unreadCount.value > 0) {
      unreadCount.value -= 1
    }
    recentUnread.value = recentUnread.value.filter((n) => n.id !== id)
    try {
      await notificationsApi.remove(id)
    } catch {
      // 失败静默，让用户重试
    }
  }

  /**
   * 用于 markRead 后立即跳转：把乐观状态保留，跳转交给调用方
   */
  async function markReadAndGo(id: number) {
    await markRead(id)
  }

  return {
    unreadCount,
    recentUnread,
    loading,
    startPolling,
    stopPolling,
    reset,
    fetchOnce,
    markRead,
    markAllRead,
    remove,
    markReadAndGo,
  }
})
